/**
 * كتالوج الصلاحيات.
 *
 * قاعدة أساسية: اسم المنصب لا يمنح أي صلاحية. الصلاحيات تأتي من هذا الكتالوج
 * وتُربط بالأدوار، ونطاق الوصول يُحدَّد بشكل مستقل. لذلك يمكن إنشاء منصبين
 * بنفس الاسم بصلاحيات مختلفة تمامًا.
 *
 * عند إضافة صلاحية جديدة: تُضاف هنا + إلى migration سياسات RLS المعنية.
 * لا تفترض أن إخفاء عنصر في الواجهة حماية.
 */

export const PERMISSIONS = [
  // --- المهام ---
  'task.view',
  'task.create',
  'task.update',
  'task.delete',
  'task.cancel',
  'task.assign',
  'task.share',
  'task.complete',
  'task.review',
  'task.approve',
  'task.return',
  'task.reopen',
  'task.template.manage',
  'task.category.manage',
  'personal_task.view_others',

  // --- المرفقات ---
  'attachment.upload',
  'attachment.delete',
  'attachment.retention.manage',

  // --- المستخدمون والتنظيم ---
  'user.view',
  'user.manage',
  'membership.manage',
  'role.view',
  'role.manage',
  'supervision.manage',
  'company.view',
  'company.manage',
  'brand.manage',
  'branch.manage',
  'company.settings.manage',

  // --- التقارير والتدقيق ---
  'report.view',
  'report.export',
  'audit.view',

  // --- المنصة (لا تُمنح إلا عبر جدول مالكي المنصة) ---
  'platform.company.manage',
  'platform.user.manage',
  'platform.role.manage',
  'platform.settings.manage',
  'platform.module.manage',
  'platform.audit.view',
  'platform.owner.manage',
] as const;

export type PermissionKey = (typeof PERMISSIONS)[number];

export type PermissionGroup = 'tasks' | 'attachments' | 'organization' | 'reports' | 'platform';

export interface PermissionDefinition {
  key: PermissionKey;
  group: PermissionGroup;
  label: string;
  description: string;
  /** صلاحيات المنصة لا تُمنح لأدوار الشركات إطلاقًا */
  platformOnly: boolean;
}

function def(
  key: PermissionKey,
  group: PermissionGroup,
  label: string,
  description: string,
  platformOnly = false,
): PermissionDefinition {
  return { key, group, label, description, platformOnly };
}

export const PERMISSION_CATALOG: readonly PermissionDefinition[] = [
  def('task.view', 'tasks', 'عرض المهام', 'رؤية المهام داخل نطاق الوصول المحدد'),
  def('task.create', 'tasks', 'إنشاء المهام', 'إنشاء مهمة جديدة وإسنادها'),
  def('task.update', 'tasks', 'تعديل المهام', 'تعديل تفاصيل المهمة والموعد النهائي'),
  def('task.delete', 'tasks', 'حذف المهام نهائيًا', 'حذف المهمة وسجل حالاتها وإشعاراتها ومرفقاتها من التخزين نهائيًا'),
  def('task.cancel', 'tasks', 'إلغاء المهام', 'إلغاء مهمة مع تسجيل السبب'),
  def('task.assign', 'tasks', 'إسناد المهام', 'تغيير المسؤولين عن المهمة'),
  def(
    'task.share',
    'tasks',
    'مشاركة المهام الشخصية',
    'إسناد مهمة شخصية لشخص آخر أو توسيع رؤيتها؛ الصلاحية منفصلة عن إنشاء المهمة',
  ),
  def('task.complete', 'tasks', 'إكمال المهمة', 'تسجيل إنجاز المهمة ورفع الإثباتات'),
  def('task.review', 'tasks', 'مراجعة الإنجاز', 'الاطلاع على إنجاز الآخرين ومراجعته'),
  def('task.approve', 'tasks', 'اعتماد المهمة', 'اعتماد الإنجاز نهائيًا'),
  def('task.return', 'tasks', 'إرجاع للتصحيح', 'رفض الاعتماد وإرجاع المهمة مع السبب'),
  def('task.reopen', 'tasks', 'إعادة فتح المهمة', 'إعادة مهمة مكتملة إلى قيد التنفيذ'),
  def('task.template.manage', 'tasks', 'إدارة قوالب التكرار', 'إنشاء وتعديل قوالب المهام المتكررة'),
  def('task.category.manage', 'tasks', 'إدارة فئات المهام', 'إنشاء وتعديل فئات المهام'),
  def(
    'personal_task.view_others',
    'tasks',
    'الاطلاع الإداري على المهام الشخصية',
    'صلاحية صريحة ومقيّدة للاطلاع على مهام شخصية للآخرين، وتُسجَّل في التدقيق',
  ),

  def('attachment.upload', 'attachments', 'رفع المرفقات', 'رفع صور ومستندات مرتبطة بالمهمة'),
  def('attachment.delete', 'attachments', 'حذف المرفقات', 'حذف مرفق يدويًا مع تسجيل العملية'),
  def(
    'attachment.retention.manage',
    'attachments',
    'إدارة سياسات الاحتفاظ',
    'تحديد مدة الاحتفاظ بالصور وتنفيذ الحذف وفق السياسة',
  ),

  def('user.view', 'organization', 'عرض المستخدمين', 'رؤية بيانات المستخدمين وعضوياتهم'),
  def('user.manage', 'organization', 'إدارة المستخدمين', 'إضافة وتعديل وتعطيل المستخدمين'),
  def('membership.manage', 'organization', 'إدارة العضويات', 'ربط المستخدم بالمنشأة وتعديل حالته'),
  def('role.view', 'organization', 'عرض الأدوار', 'رؤية الأدوار والصلاحيات'),
  def('role.manage', 'organization', 'إدارة الأدوار', 'إنشاء الأدوار وتعديل صلاحياتها ونطاقاتها'),
  def('supervision.manage', 'organization', 'إدارة الإشراف', 'تحديد من يراقب من وبنطاق إشرافه'),
  def('company.view', 'organization', 'عرض المنشأة', 'رؤية بيانات المنشأة'),
  def('company.manage', 'organization', 'إدارة المنشأة', 'تعديل بيانات المنشأة وحالتها'),
  def('brand.manage', 'organization', 'إدارة البراندات', 'إنشاء وتعديل البراندات'),
  def('branch.manage', 'organization', 'إدارة الفروع', 'إنشاء وتعديل الفروع وربطها بالبراندات'),
  def('company.settings.manage', 'organization', 'إعدادات المنشأة', 'تعديل الإعدادات التشغيلية والمظهر'),

  def('report.view', 'reports', 'عرض التقارير', 'رؤية لوحات المتابعة والتقارير داخل النطاق'),
  def('report.export', 'reports', 'تصدير التقارير', 'تصدير نتائج التقارير'),
  def('audit.view', 'reports', 'عرض سجل التدقيق', 'رؤية العمليات الإدارية الحساسة'),

  def('platform.company.manage', 'platform', 'إدارة منشآت المنصة', 'إنشاء وتعطيل أي منشأة', true),
  def('platform.user.manage', 'platform', 'إدارة مستخدمي المنصة', 'إدارة الحسابات على مستوى المنصة', true),
  def('platform.role.manage', 'platform', 'إدارة أدوار المنصة', 'إنشاء قوالب الأدوار العامة', true),
  def('platform.settings.manage', 'platform', 'إعدادات المنصة', 'الإعدادات العامة والافتراضية', true),
  def('platform.module.manage', 'platform', 'إدارة وحدات النظام', 'تمكين الوحدات لكل منشأة', true),
  def('platform.audit.view', 'platform', 'تدقيق المنصة', 'مراجعة كل سجلات التدقيق', true),
  def('platform.owner.manage', 'platform', 'إدارة مالكي المنصة', 'منح وسحب صلاحية مالك المنصة', true),
];

export const PERMISSION_MAP: ReadonlyMap<PermissionKey, PermissionDefinition> = new Map(
  PERMISSION_CATALOG.map((p) => [p.key, p]),
);

export function isPermissionKey(value: string): value is PermissionKey {
  return PERMISSION_MAP.has(value as PermissionKey);
}

export const COMPANY_PERMISSIONS: readonly PermissionKey[] = PERMISSION_CATALOG.filter(
  (p) => !p.platformOnly,
).map((p) => p.key);

export const PLATFORM_PERMISSIONS: readonly PermissionKey[] = PERMISSION_CATALOG.filter(
  (p) => p.platformOnly,
).map((p) => p.key);

/** صلاحيات تشغيل المنصة التي يمكن للمالك تفويضها؛ ملكية المنصة نفسها غير قابلة للتفويض. */
export const DELEGABLE_PLATFORM_PERMISSIONS: readonly PermissionKey[] = PLATFORM_PERMISSIONS.filter(
  (permission) => permission !== 'platform.owner.manage',
);

export const PERMISSION_GROUP_LABELS: Record<PermissionGroup, string> = {
  tasks: 'المهام',
  attachments: 'المرفقات والخلفية',
  organization: 'المستخدمون والتنظيم',
  reports: 'التقارير والتدقيق',
  platform: 'صلاحيات المنصة',
};

/* =============================================================================
 * قوالب الأدوار الأولية
 *
 * تُستخدم عند تهيئة شركة جديدة لإنشاء أدوار جاهزة قابلة للتعديل بالكامل من
 * الواجهة. ليست قيدًا برمجيًا: حذفها أو تعديلها لا يمس الكود.
 * ========================================================================== */

export interface RoleBlueprint {
  key: string;
  name: string;
  description: string;
  priority: number;
  permissions: PermissionKey[];
}

const VIEWER_TASK_PERMISSIONS: PermissionKey[] = [
  'task.view',
  'task.complete',
  'task.review',
  'attachment.upload',
  'attachment.delete',
];

export const DEFAULT_ROLE_BLUEPRINTS: readonly RoleBlueprint[] = [
  {
    key: 'company_admin',
    name: 'مسؤول المنشأة',
    description: 'إدارة كاملة لبيانات المنشأة والمستخدمين والمهام',
    priority: 20,
    permissions: [...COMPANY_PERMISSIONS].filter((p) => p !== 'personal_task.view_others'),
  },
  {
    key: 'brands_manager',
    name: 'مدير فروع',
    description: 'متابعة مدراء الفروع والإشراف على مهامهم',
    priority: 30,
    permissions: [
      'task.view',
      'task.create',
      'task.update',
      'task.delete',
      'task.assign',
      'task.complete',
      'task.review',
      'task.approve',
      'task.return',
      'task.cancel',
      'attachment.upload',
      'attachment.delete',
      'user.view',
      'role.view',
      'company.view',
      'report.view',
      'report.export',
      'supervision.manage',
    ],
  },
  {
    key: 'branch_manager',
    name: 'مدير فرع',
    description: 'إنشاء مهام الفرع ومتابعة تنفيذها واعتمادها',
    priority: 40,
    permissions: [
      'task.view',
      'task.create',
      'task.update',
      'task.delete',
      'task.assign',
      'task.share',
      'task.complete',
      'task.review',
      'task.approve',
      'task.return',
      'task.cancel',
      'task.template.manage',
      'task.category.manage',
      'attachment.upload',
      'attachment.delete',
      'user.view',
      'report.view',
    ],
  },
  {
    key: 'hr_supervisor',
    name: 'HR',
    description: 'دور إشرافي قابل للتخصيص لمراقبة الالتزام، دون صلاحيات مالية أو حضور',
    priority: 45,
    permissions: ['task.view', 'task.review', 'user.view', 'report.view', 'report.export', 'supervision.manage'],
  },
  {
    key: 'supervisor',
    name: 'مشرف',
    description: 'متابعة مهام الفريق وتكليفهم دون اعتماد نهائي',
    priority: 50,
    permissions: [
      'task.view',
      'task.create',
      'task.update',
      'task.assign',
      'task.share',
      'task.complete',
      'task.review',
      'attachment.upload',
      'user.view',
      'report.view',
    ],
  },
  {
    key: 'worker',
    name: 'عامل',
    description: 'تنفيذ المهام المسندة ورفع الإثباتات',
    priority: 80,
    permissions: VIEWER_TASK_PERMISSIONS,
  },
  {
    key: 'cashier',
    name: 'كاشير',
    description: 'تنفيذ مهام نقطة البيع ورفع الإثباتات',
    priority: 80,
    permissions: VIEWER_TASK_PERMISSIONS,
  },
];

/** النطاقات الافتراضية لكل قالب دور عند تهيئة شركة جديدة. */
export const DEFAULT_ROLE_SCOPES: Record<string, Array<{ scopeType: string; targetId: null }>> = {
  company_admin: [{ scopeType: 'company', targetId: null }],
  brands_manager: [{ scopeType: 'company', targetId: null }],
  branch_manager: [{ scopeType: 'branch', targetId: null }],
  hr_supervisor: [{ scopeType: 'supervised', targetId: null }],
  supervisor: [{ scopeType: 'branch', targetId: null }],
  worker: [{ scopeType: 'self', targetId: null }],
  cashier: [{ scopeType: 'self', targetId: null }],
};
